import { useMemo, useState, type ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';

import {
  useMenuMidSectionMembersQuery,
  useMenuRootsQuery,
  useMenuSecBarsQuery,
} from '@/client/api/menus/menus.query';
import {
  FOOTER_MENU_ROOTS,
  MENU_SEC_PILLARS,
  MenuSecHierarchyNode,
} from '@/client/types/menus';
import { Skeleton } from '@/components/ui/skeleton';

type SurfaceId = 'header' | 'page' | 'footer';

interface SiteMenuMapProps {
  active: SurfaceId;
  onSelect: (id: SurfaceId) => void;
}

const PRIMARY_PILLARS = MENU_SEC_PILLARS.filter((p) => p.inPrimaryNav);

const isSecVisible = (n: MenuSecHierarchyNode) => n.menu_sec === true || n.menu_sec === 1;

const nodeLabel = (n: MenuSecHierarchyNode) =>
  n.name_menu || n.name_friendly || n.name || `Entity #${n.etid}`;

const Chip = ({ children, tone = 'light' }: { children: ReactNode; tone?: 'light' | 'dark' | 'blue' }) => (
  <span
    className={
      tone === 'dark'
        ? 'rounded bg-slate-800 px-1.5 py-1 text-center text-[10px] font-medium text-slate-100'
        : tone === 'blue'
          ? 'rounded border border-blue-200/60 bg-blue-100/90 px-2 py-0.5 text-[10px] font-medium text-blue-900'
          : 'rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] text-gray-700'
    }
  >
    {children}
  </span>
);

/**
 * Live preview of public site menus — same admin endpoints the editors use.
 * Pillar labels match the public primary nav; blue-bar / chips / footer are API-driven.
 */
export const SiteMenuMap = ({ active, onSelect }: SiteMenuMapProps) => {
  const [previewPillar, setPreviewPillar] = useState(PRIMARY_PILLARS[0]?.id ?? 'market');

  const secQueries = useMenuSecBarsQuery(
    PRIMARY_PILLARS.map((p) => p.id),
    { sec_menu_active: true },
  );
  const { data: homepageStrip, isLoading: stripLoading } = useMenuMidSectionMembersQuery('main');
  const { data: roots = [], isLoading: rootsLoading } = useMenuRootsQuery();

  const secByPillar = useMemo(() => {
    const map = new Map<string, { loading: boolean; error: boolean; l1: MenuSecHierarchyNode[] }>();
    PRIMARY_PILLARS.forEach((p, i) => {
      const q = secQueries[i];
      const children = q?.data?.hierarchy?.children ?? [];
      const l1 = [...children]
        .filter(isSecVisible)
        .sort((a, b) => (a.order_by ?? 0) - (b.order_by ?? 0));
      map.set(p.id, {
        loading: !!q?.isLoading,
        error: !!q?.isError,
        l1,
      });
    });
    return map;
  }, [secQueries]);

  const previewBar = secByPillar.get(previewPillar);
  const homepageChips = useMemo(() => {
    const members = homepageStrip?.members ?? [];
    return [...members]
      .sort(
        (a, b) =>
          (a.menu_mid_main_order ?? a.order_by ?? 0) - (b.menu_mid_main_order ?? b.order_by ?? 0),
      )
      .slice(0, 10)
      .map((m) => m.name_menu || m.name_friendly || m.name || `#${m.etid}`);
  }, [homepageStrip]);

  const footerColumns = useMemo(() => {
    return FOOTER_MENU_ROOTS.map((fr) => {
      const live = roots.find((r) => r.menid === fr.menid);
      return {
        menid: fr.menid,
        label: live?.name || fr.label,
        count: live?.node_count,
      };
    });
  }, [roots]);

  const card = (id: SurfaceId, title: string, body: ReactNode) => {
    const isActive = active === id;
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(id);
          }
        }}
        className={`w-full cursor-pointer rounded-lg border px-4 py-3 text-left transition-colors ${
          isActive
            ? 'border-blue-300 bg-blue-50 ring-1 ring-blue-200'
            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
        }`}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-gray-900">{title}</span>
          {isActive && (
            <span className="text-[10px] font-medium uppercase tracking-wide text-blue-700">
              Editing
            </span>
          )}
        </div>
        {body}
      </div>
    );
  };

  return (
    <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">Live site menus</h2>
        <p className="text-[11px] text-gray-500">
          From admin menus APIs · click a layer to edit
        </p>
      </div>

      <div className="space-y-2">
        {card(
          'header',
          '1. Header — primary pillars + blue category bar',
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {PRIMARY_PILLARS.map((p) => {
                const info = secByPillar.get(p.id);
                const selected = previewPillar === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewPillar(p.id);
                      onSelect('header');
                    }}
                    className={`rounded border px-2 py-0.5 text-xs font-medium transition-colors ${
                      selected
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 bg-white text-gray-800 hover:border-gray-400'
                    }`}
                  >
                    {p.primaryNavLabel}
                    {info && !info.loading && (
                      <span className={`ml-1 ${selected ? 'text-gray-300' : 'text-gray-400'}`}>
                        {info.l1.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
              <ArrowRight className="h-3 w-3 flex-shrink-0" />
              Blue bar for{' '}
              {PRIMARY_PILLARS.find((p) => p.id === previewPillar)?.primaryNavLabel ?? previewPillar}
            </div>

            <div className="flex flex-wrap gap-1 rounded bg-blue-100/80 px-2 py-1.5">
              {previewBar?.loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-5 w-16 rounded bg-blue-200/50" />
                ))}
              {previewBar?.error && (
                <span className="text-[11px] text-red-700">Couldn’t load category bar</span>
              )}
              {!previewBar?.loading &&
                !previewBar?.error &&
                previewBar?.l1.length === 0 && (
                  <span className="text-[11px] text-blue-800/70">No categories in this bar yet</span>
                )}
              {!previewBar?.loading &&
                previewBar?.l1.slice(0, 12).map((n) => (
                  <Chip key={n.etid} tone="blue">
                    {nodeLabel(n)}
                  </Chip>
                ))}
              {(previewBar?.l1.length ?? 0) > 12 && (
                <span className="self-center text-[10px] text-blue-800/60">
                  +{previewBar!.l1.length - 12} more
                </span>
              )}
            </div>
          </div>,
        )}

        {card(
          'page',
          '2. Homepage category strip',
          <div className="space-y-1.5">
            <div className="flex flex-wrap gap-1">
              {stripLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-5 w-20 rounded-full" />
                ))}
              {!stripLoading && homepageChips.length === 0 && (
                <span className="text-[11px] text-gray-400">No homepage strip categories</span>
              )}
              {!stripLoading &&
                homepageChips.map((name) => (
                  <Chip key={name}>{name}</Chip>
                ))}
              {(homepageStrip?.members.length ?? 0) > homepageChips.length && (
                <span className="self-center text-[10px] text-gray-400">
                  +{(homepageStrip?.members.length ?? 0) - homepageChips.length} more
                </span>
              )}
            </div>
            <p className="text-[11px] text-gray-500">
              Live <code className="text-[10px]">mid/main</code> membership · other verticals under
              Page strips
            </p>
          </div>,
        )}

        {card(
          'footer',
          '3. Footer link columns',
          <div className="space-y-1.5">
            <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 md:grid-cols-6">
              {rootsLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-7 w-full rounded bg-slate-200" />
                ))}
              {!rootsLoading &&
                footerColumns.map((col) => (
                  <Chip key={col.menid} tone="dark">
                    {col.label}
                    {col.count != null ? ` · ${col.count}` : ''}
                  </Chip>
                ))}
            </div>
            <p className="text-[11px] text-gray-500">
              Live menu roots the public footer loads · not the header pillars
            </p>
          </div>,
        )}
      </div>
    </div>
  );
};
